import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Campaign, User } from '../../types';
import { subscribeToUsers } from '../../services/users';
import { campaignRepository } from '../../platform/firebase/campaignRepository';
import {
  listTasksForCampaign,
} from '../../services/tasks';
import {
  setCampaignRequireFirstApprovalBeforeMoreClaims,
  setCampaignEscalationAdmin,
  setCampaignAutoApprove,
  setCampaignPaused,
  setCampaignPauseAllowedTaskIds,
  setCampaignSubmissionEmail,
  setCampaignAutoReleaseStaleClaims,
  setCampaignAutoReleaseStaleInProgress,
  setCampaignAutoReleaseStaleReviews,
  setCampaignMaxTasksPerUser,
  setCampaignMaxTasksPerTrustedUser,
  setCampaignMaxClaimedTasksPerUser,
  assignTaskToExpert,
  AssignTaskToExpertRequest,
  AssignTaskToExpertResult,
  CampaignTaskDomainInput,
} from '../../services/adminCampaigns';
import {
  AdjustCampaignTaskPoolResult,
  createCampaignAdminApi,
} from '../../domain/campaigns/admin';
import { firebaseCampaignAdminGateway } from '../../platform/firebase/campaignAdminGateway';
import {
  CampaignCounts,
  ExportTasksOptions,
  campaignTypeCountsKey,
} from '../AdminPage/tools/CampaignsTool/viewModel';
import {
  buildCampaignUserTaskStats,
  CampaignUserTaskStats,
} from '../AdminPage/tools/CampaignsTool/userStats';
import {
  downloadBlob,
  exportFilename,
  filterTasksByStatus,
  resolveTaskExpertIds,
  reshapeTaskRowsForExport,
  tasksToCsv,
  tasksToJson,
  toTaskAnswers,
} from '../../services/taskExport';
import { getTaskTypeDescriptor } from '../../taskTypes/descriptor';
import { logGlobalError } from '../../utils/globalErrorLogger';
import { createQueueReadController } from '../../features/taskQueue/readController';
import { ExpertSort, ExpertSortKey, nextExpertSort } from './expertSort';

const campaignAdminApi = createCampaignAdminApi(firebaseCampaignAdminGateway);

export interface AdminCampaignDetailViewModel {
  busy: boolean;
  /** True when the campaign list has loaded but no campaign with the requested
   *  id was found. The page renders a "not found" state. */
  notFound: boolean;
  error: string | null;
  campaign: Campaign | null;
  usersByExpertId: Map<string, User>;
  /** Experts on this campaign mapped to their Workbench users, sorted by
   *  display name. Uses the full Aligned stage map when available, falling
   *  back to active experts for older/manual campaign docs. */
  matchedUsers: User[];
  /** Admin accounts available as the campaign's escalation owner. */
  adminUsers: User[];
  trustedUsers: User[];
  activityUsers: User[];
  /** Aggregate (across all task types) counts. */
  counts: CampaignCounts | null;
  /** Per-type counts, keyed by `campaignTypeCountsKey`. */
  countsByType: Map<string, CampaignCounts>;
  countsLoading: boolean;
  userStats: Map<string, CampaignUserTaskStats> | null;
  userStatsLoading: boolean;
  pendingAdjust: boolean;
  pendingAssign: boolean;
  exporting: boolean;
  /** User-list filter — by name OR email substring (case-insensitive). */
  userQuery: string;
  setUserQuery: (q: string) => void;
  /** `matchedUsers` filtered by `userQuery`. */
  filteredUsers: User[];
  expertSort: ExpertSort;
  sortExpertsBy: (key: ExpertSortKey) => void;
  initialize: () => void;
  dispose: () => void;
  reloadCounts: () => Promise<void>;
  adjust: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<AdjustCampaignTaskPoolResult>;
  previewAdjust: (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => Promise<AdjustCampaignTaskPoolResult>;
  /** Assign a task to an expert as though they had claimed it. Pass a
   *  `taskId` to hand over an existing available task, or omit it to create a
   *  fresh blank task in the domain. */
  assignTask: (
    targetUid: string,
    req: Omit<AssignTaskToExpertRequest, 'campaignId' | 'targetUid'>,
  ) => Promise<AssignTaskToExpertResult>;
  setDomains: (taskTypeId: string, domains: CampaignTaskDomainInput[]) => Promise<void>;
  setLlmReview: (taskTypeId: string, enabled: boolean) => Promise<void>;
  setLlmValidator: (taskTypeId: string, enabled: boolean) => Promise<void>;
  setSubmissionEmail: (enabled: boolean) => Promise<void>;
  setAutoApprove: (enabled: boolean) => Promise<void>;
  setPaused: (paused: boolean) => Promise<void>;
  setPauseAllowedTaskIds: (taskIds: string[]) => Promise<void>;
  setClaimingPaused: (taskTypeId: string, paused: boolean, domainId?: string | null) => Promise<void>;
  setRequireFirstApprovalBeforeMoreClaims: (enabled: boolean) => Promise<void>;
  setEscalationAdmin: (adminId: string | null) => Promise<void>;
  setAutoReleaseStaleClaims: (enabled: boolean, days: number) => Promise<void>;
  setAutoReleaseStaleInProgress: (enabled: boolean, days: number) => Promise<void>;
  setAutoReleaseStaleReviews: (enabled: boolean, hours: number) => Promise<void>;
  setMaxTasksPerUser: (maxTasksPerUser: number | null) => Promise<void>;
  setMaxTasksPerTrustedUser: (value: number | null) => Promise<void>;
  setMaxClaimedTasksPerUser: (maxClaimedTasksPerUser: number | null) => Promise<void>;
  exportTasks: (options: ExportTasksOptions) => Promise<void>;
}

export function useAdminCampaignDetailViewModel(
  campaignId: string,
): AdminCampaignDetailViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  // Counts come from the same read-controller seam the queue uses, so
  // there is one data path to campaign status counts (M88).
  const readControllerRef = useRef(createQueueReadController());
  const readController = readControllerRef.current;

  const [error, setError] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState<boolean>(false);
  const [users, setUsers] = useState<User[]>([]);
  const [counts, setCounts] = useState<CampaignCounts | null>(null);
  const [countsByType, setCountsByType] = useState<Map<string, CampaignCounts>>(new Map());
  const [countsLoading, setCountsLoading] = useState<boolean>(false);
  const [userStats, setUserStats] = useState<Map<string, CampaignUserTaskStats> | null>(null);
  const [userStatsLoading, setUserStatsLoading] = useState<boolean>(false);
  const [pendingAdjust, setPendingAdjust] = useState<boolean>(false);
  const [pendingAssign, setPendingAssign] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [userQuery, setUserQuery] = useState<string>('');
  const [expertSort, setExpertSort] = useState<ExpertSort>({ key: 'expert', direction: 'ascending' });
  const sortExpertsBy = (key: ExpertSortKey) => setExpertSort((current) => nextExpertSort(current, key));
  const unsubsRef = useRef<Array<() => void>>([]);

  const dispose = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
  }, []);

  const initialize = useCallback(() => {
    setBusy(true);
    setError(null);
    setCampaignsLoaded(false);
    dispose();

    let campaignsReady = false;
    let usersReady = false;
    const maybeDone = () => {
      if (campaignsReady && usersReady) setBusy(false);
    };

    const unsubCampaigns = campaignRepository.subscribeAll(
      (next) => {
        setCampaigns(next);
        setCampaignsLoaded(true);
        campaignsReady = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    const unsubUsers = subscribeToUsers(
      (next) => {
        setUsers(next);
        usersReady = true;
        maybeDone();
      },
      (err) => {
        setError(err.message);
        setBusy(false);
      },
    );
    unsubsRef.current = [unsubCampaigns, unsubUsers];
  }, [dispose]);

  const campaign = useMemo(
    () => campaigns.find((c) => c.id === campaignId) ?? null,
    [campaigns, campaignId],
  );
  const notFound = campaignsLoaded && !campaign;

  const usersByExpertId = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) {
      if (u.expertId) m.set(u.expertId, u);
    }
    return m;
  }, [users]);

  const usersByUid = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u.uid, u);
    return m;
  }, [users]);

  const usersByUidRef = useRef(usersByUid);
  usersByUidRef.current = usersByUid;

  const matchedUsers = useMemo<User[]>(() => {
    if (!campaign) return [];
    const expertIds = campaign.alignedCampaignExpertStatuses
      ? Object.keys(campaign.alignedCampaignExpertStatuses)
      : campaign.activeExpertIds;
    return expertIds
      .map((id) => usersByExpertId.get(id))
      .filter((u): u is User => !!u)
      .sort((a, b) =>
        (a.displayName ?? a.email ?? '').localeCompare(b.displayName ?? b.email ?? ''),
      );
  }, [campaign, usersByExpertId]);

  const trustedUsers = useMemo(() => users
    .filter((user) => user.trustedCampaignIds?.includes(campaignId))
    .sort((a, b) => (a.email ?? '').localeCompare(b.email ?? '')), [users, campaignId]);

  const adminUsers = useMemo<User[]>(() => users
    .filter((user) => user.isAdmin === true && !!user.email?.trim())
    .sort((a, b) => (a.displayName ?? a.email ?? '').localeCompare(
      b.displayName ?? b.email ?? '',
    )), [users]);

  const filteredUsers = useMemo<User[]>(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return matchedUsers;
    return matchedUsers.filter((u) => {
      const name = (u.displayName ?? '').toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [matchedUsers, userQuery]);

  // Lazy-load counts + user stats once the campaign resolves. Re-fires when
  // the bound `taskTypes` array changes so per-type bars stay in sync after
  // an admin adds or removes a type.
  const taskTypeIdsKey = useMemo(
    () => campaign?.taskTypes
      .map((t) => `${t.taskTypeId}:${(t.domains ?? []).map((d) => `${d.domainId}=${d.targetCount}`).join('|')}`)
      .join(',') ?? '',
    [campaign],
  );

  const reloadCounts = useCallback(async () => {
    if (!campaign) return;
    setCountsLoading(true);
    setUserStatsLoading(true);
    try {
      const scopes = campaign.taskTypes.flatMap((t) => [
        { taskTypeId: t.taskTypeId, domainId: null as string | null },
        ...((t.domains ?? []).map((d) => ({ taskTypeId: t.taskTypeId, domainId: d.domainId }))),
      ]);
      const [aggregate, ...perScope] = await Promise.all([
        readController.countTasksByStatus(campaign.id),
        ...scopes.map((scope) => readController.countTasksByStatus(
          campaign.id, scope.taskTypeId, scope.domainId,
        )),
      ]);
      setCounts(aggregate);
      setCountsByType((prev) => {
        const next = new Map(prev);
        scopes.forEach((scope, i) => {
          next.set(campaignTypeCountsKey(campaign.id, scope.taskTypeId, scope.domainId), perScope[i]);
        });
        return next;
      });
      const tasks = await listTasksForCampaign(campaign.id);
      setUserStats(buildCampaignUserTaskStats(tasks));
    } catch (e: any) {
      setError(e?.message || 'Failed to load campaign stats');
      logGlobalError(e, {
        context: 'AdminCampaignDetail.reloadCounts',
        metadata: { campaignId: campaign.id },
      });
    } finally {
      setCountsLoading(false);
      setUserStatsLoading(false);
    }
  }, [campaign]);

  // Auto-load on first resolve and whenever the bound type set changes.
  // The effect depends on stable scalars so it doesn't re-fire on every
  // `reloadCounts` identity change. We keep a ref so the effect always reads
  // the latest closure.
  const reloadCountsRef = useRef(reloadCounts);
  reloadCountsRef.current = reloadCounts;
  const resolvedCampaignId = campaign?.id ?? null;
  useEffect(() => {
    if (!resolvedCampaignId) return;
    void reloadCountsRef.current();
  }, [resolvedCampaignId, taskTypeIdsKey]);

  const adjust = useCallback(
    async (taskTypeId: string, targetCount: number, domainId?: string | null, domainLabel?: string | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setPendingAdjust(true);
      setError(null);
      try {
        const result = await campaignAdminApi.adjustTaskPool({
          campaignId: campaign.id,
          taskTypeId,
          targetCount,
          domainId,
          domainLabel,
        });
        await reloadCounts();
        return result;
      } catch (e: any) {
        setError(e?.message || 'Failed to adjust tasks');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.adjust',
          metadata: { campaignId: campaign.id, taskTypeId, targetCount, domainId },
        });
        throw e;
      } finally {
        setPendingAdjust(false);
      }
    },
    [campaign, reloadCounts],
  );

  const previewAdjust = useCallback(
    async (
      taskTypeId: string,
      targetCount: number,
      domainId?: string | null,
      domainLabel?: string | null,
    ) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setPendingAdjust(true);
      setError(null);
      try {
        return await campaignAdminApi.previewTaskPool({
          campaignId: campaign.id,
          taskTypeId,
          targetCount,
          domainId,
          domainLabel,
        });
      } catch (e: any) {
        setError(e?.message || 'Failed to preview task adjustment');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.previewAdjust',
          metadata: { campaignId: campaign.id, taskTypeId, targetCount, domainId },
        });
        throw e;
      } finally {
        setPendingAdjust(false);
      }
    },
    [campaign],
  );

  const assignTask = useCallback(
    async (
      targetUid: string,
      req: Omit<AssignTaskToExpertRequest, 'campaignId' | 'targetUid'>,
    ) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setPendingAssign(true);
      setError(null);
      try {
        const result = await assignTaskToExpert({
          campaignId: campaign.id,
          targetUid,
          ...req,
        });
        await reloadCounts();
        return result;
      } catch (e: any) {
        setError(e?.message || 'Failed to assign task');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.assignTask',
          metadata: {
            campaignId: campaign.id,
            targetUid,
            taskTypeId: req.taskTypeId,
            domainId: req.domainId,
            taskId: req.taskId,
          },
        });
        throw e;
      } finally {
        setPendingAssign(false);
      }
    },
    [campaign, reloadCounts],
  );

  const setDomains = useCallback(
    async (taskTypeId: string, domains: CampaignTaskDomainInput[]) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setPendingAdjust(true);
      setError(null);
      try {
        await campaignAdminApi.updateTaskTypeSettings({
          campaignId: campaign.id,
          taskTypeId,
          changes: [{ kind: 'domains', value: domains }],
        });
        await reloadCounts();
      } catch (e: any) {
        setError(e?.message || 'Failed to update domains');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setDomains',
          metadata: { campaignId: campaign.id, taskTypeId },
        });
        throw e;
      } finally {
        setPendingAdjust(false);
      }
    },
    [campaign, reloadCounts],
  );

  const setLlmReview = useCallback(
    async (taskTypeId: string, enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await campaignAdminApi.updateTaskTypeSettings({
          campaignId: campaign.id,
          taskTypeId,
          changes: [{ kind: 'llmReview', enabled }],
        });
      } catch (e: any) {
        setError(e?.message || 'Failed to update AI pre-review setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setLlmReview',
          metadata: { campaignId: campaign.id, taskTypeId, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setLlmValidator = useCallback(
    async (taskTypeId: string, enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await campaignAdminApi.updateTaskTypeSettings({
          campaignId: campaign.id,
          taskTypeId,
          changes: [{ kind: 'llmValidator', enabled }],
        });
      } catch (e: any) {
        setError(e?.message || 'Failed to update AI pre-submit validation setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setLlmValidator',
          metadata: { campaignId: campaign.id, taskTypeId, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setSubmissionEmail = useCallback(
    async (enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignSubmissionEmail(campaign.id, enabled);
      } catch (e: any) {
        setError(e?.message || 'Failed to update submission email setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setSubmissionEmail',
          metadata: { campaignId: campaign.id, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setPauseAllowedTaskIds = useCallback(
    async (taskIds: string[]) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignPauseAllowedTaskIds(campaign.id, taskIds);
      } catch (e: any) {
        setError(e?.message || 'Failed to update allowed tasks');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setPauseAllowedTaskIds',
          metadata: { campaignId: campaign.id },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setPaused = useCallback(
    async (enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignPaused(campaign.id, enabled);
      } catch (e: any) {
        setError(e?.message || 'Failed to update campaign pause setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setPaused',
          metadata: { campaignId: campaign.id, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setAutoApprove = useCallback(
    async (enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignAutoApprove(campaign.id, enabled);
      } catch (e: any) {
        setError(e?.message || 'Failed to update review routing setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setAutoApprove',
          metadata: { campaignId: campaign.id, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setClaimingPaused = useCallback(
    async (taskTypeId: string, paused: boolean, domainId?: string | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await campaignAdminApi.updateTaskTypeSettings({
          campaignId: campaign.id,
          taskTypeId,
          changes: [domainId
            ? { kind: 'domainClaimingPaused', domainId, enabled: paused }
            : { kind: 'claimingPaused', enabled: paused }],
        });
      } catch (e: any) {
        setError(e?.message || 'Failed to update claiming pause setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setClaimingPaused',
          metadata: { campaignId: campaign.id, taskTypeId, domainId, paused },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setRequireFirstApprovalBeforeMoreClaims = useCallback(
    async (enabled: boolean) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignRequireFirstApprovalBeforeMoreClaims(campaign.id, enabled);
      } catch (e: any) {
        setError(e?.message || 'Failed to update claim quality gate');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setRequireFirstApprovalBeforeMoreClaims',
          metadata: { campaignId: campaign.id, enabled },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setEscalationAdmin = useCallback(
    async (adminId: string | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignEscalationAdmin(campaign.id, adminId);
      } catch (e: any) {
        setError(e?.message || 'Failed to update escalation admin');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setEscalationAdmin',
          metadata: { campaignId: campaign.id, adminId },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setAutoReleaseStaleClaims = useCallback(
    async (enabled: boolean, days: number) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignAutoReleaseStaleClaims(campaign.id, enabled, days);
      } catch (e: any) {
        setError(e?.message || 'Failed to update stale-claim release setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setAutoReleaseStaleClaims',
          metadata: { campaignId: campaign.id, enabled, days },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setAutoReleaseStaleInProgress = useCallback(
    async (enabled: boolean, days: number) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignAutoReleaseStaleInProgress(campaign.id, enabled, days);
      } catch (e: any) {
        setError(e?.message || 'Failed to update stale in-progress release setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setAutoReleaseStaleInProgress',
          metadata: { campaignId: campaign.id, enabled, days },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setAutoReleaseStaleReviews = useCallback(
    async (enabled: boolean, hours: number) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignAutoReleaseStaleReviews(campaign.id, enabled, hours);
      } catch (e: any) {
        setError(e?.message || 'Failed to update inactive review release setting');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setAutoReleaseStaleReviews',
          metadata: { campaignId: campaign.id, enabled, hours },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setMaxTasksPerUser = useCallback(
    async (maxTasksPerUser: number | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignMaxTasksPerUser(campaign.id, maxTasksPerUser);
      } catch (e: any) {
        setError(e?.message || 'Failed to update max tasks per user');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setMaxTasksPerUser',
          metadata: { campaignId: campaign.id, maxTasksPerUser },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setMaxTasksPerTrustedUser = useCallback(
    async (maxTasksPerTrustedUser: number | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignMaxTasksPerTrustedUser(campaign.id, maxTasksPerTrustedUser);
      } catch (e: any) {
        setError(e?.message || 'Failed to update max tasks per user');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setMaxTasksPerTrustedUser',
          metadata: { campaignId: campaign.id, maxTasksPerTrustedUser },
        });
        throw e;
      }
    },
    [campaign],
  );

  const setMaxClaimedTasksPerUser = useCallback(
    async (maxClaimedTasksPerUser: number | null) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setError(null);
      try {
        await setCampaignMaxClaimedTasksPerUser(campaign.id, maxClaimedTasksPerUser);
      } catch (e: any) {
        setError(e?.message || 'Failed to update max claimed tasks per user');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.setMaxClaimedTasksPerUser',
          metadata: { campaignId: campaign.id, maxClaimedTasksPerUser },
        });
        throw e;
      }
    },
    [campaign],
  );

  const exportTasks = useCallback(
    async ({ format, statusFilter, exportType, taskTypeId }: ExportTasksOptions) => {
      if (!campaign) throw new Error('Campaign not loaded');
      setExporting(true);
      setError(null);
      try {
        const rawTasks = await listTasksForCampaign(campaign.id);
        const scoped = rawTasks.filter((t) => t.taskType === taskTypeId);
        const withExpert = resolveTaskExpertIds(scoped, usersByUidRef.current);
        const filtered = filterTasksByStatus(withExpert, statusFilter);
        const rows = exportType === 'answers' ? toTaskAnswers(filtered) : filtered;
        const runtime = await getTaskTypeDescriptor(taskTypeId).loadRuntime();
        const reshapeResult = runtime.reshapeResultForExport;
        const content = format === 'csv'
          ? tasksToCsv(reshapeTaskRowsForExport(rows, reshapeResult))
          : tasksToJson(rows, { reshapeResult });
        const mime = format === 'csv' ? 'text/csv;charset=utf-8' : 'application/json;charset=utf-8';
        downloadBlob(content, exportFilename(campaign.id, format, { taskTypeId }), mime);
      } catch (e: any) {
        setError(e?.message || 'Failed to export tasks');
        logGlobalError(e, {
          context: 'AdminCampaignDetail.exportTasks',
          metadata: { campaignId: campaign.id, taskTypeId, format, statusFilter, exportType },
        });
        throw e;
      } finally {
        setExporting(false);
      }
    },
    [campaign],
  );

  return {
    busy,
    notFound,
    error,
    campaign,
    usersByExpertId,
    matchedUsers,
    adminUsers,
    trustedUsers,
    activityUsers: users,
    counts,
    countsByType,
    countsLoading,
    userStats,
    userStatsLoading,
    pendingAdjust,
    pendingAssign,
    exporting,
    userQuery,
    setUserQuery,
    filteredUsers,
    expertSort,
    sortExpertsBy,
    initialize,
    dispose,
    reloadCounts,
    adjust,
    previewAdjust,
    assignTask,
    setDomains,
    setLlmReview,
    setLlmValidator,
    setSubmissionEmail,
    setAutoApprove,
    setPaused,
    setPauseAllowedTaskIds,
    setClaimingPaused,
    setRequireFirstApprovalBeforeMoreClaims,
    setEscalationAdmin,
    setAutoReleaseStaleClaims,
    setAutoReleaseStaleInProgress,
    setAutoReleaseStaleReviews,
    setMaxTasksPerUser,
    setMaxTasksPerTrustedUser,
    setMaxClaimedTasksPerUser,
    exportTasks,
  };
}
